import { useCallback, useEffect, useMemo, useState } from 'react';
import api, { ClientDirectoryUser, ClientServiceProvider, SalesClient } from 'api';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'redux/store';
import { loadSalesClients } from 'redux/sales-clients/sales-clients.middleware';
import { useWindowSize } from 'hooks/useWindowSize';
import SelectStatusDropdown from '../../../select-status-dropdown/SelectStatusDropdown';
import SelectVisaProgramDropdown from '../select-visa-program-dropdown/SelectVisaProgramDropdown';
import SearchClientBar from '../search-client-bar/SearchClientBar';
import { ClientApplicationStatus } from 'api/models/ClientApplicationStatus';
import { PaginatedItems } from 'components/common/pagination';
import {
    SearchbarContainerMobileForDashboard,
    TopContainer,
} from '../../../../../Dashboard.styled';
import { DashboardFilter, DashboardView } from './types';
import { useDashboardView } from './DashboardViewContext';
import CasesTable, { CasesTableProps } from './cases-table/CasesTable';
import { ClientsTableClient } from './clients-table/ClientsTable';
import UsersTable from './clients-table/ClientsTable';
import CompaniesTable, { CompaniesTableCompany } from './companies-table/CompaniesTable';
import { DropdownOption } from 'components/common/OptionDropdown/OptionDropdown';
import { RootState as StoreRootState } from 'redux/store';
import PERMISSION from 'assets/permissions.json';
import { FEATURE } from 'constant/features';
import {
    CASES_ID_SEARCH_DEBOUNCE_MS,
    isCasesIdType,
    isNumericCaseId,
    resolveCasesIdQuery,
    toCasesQueryFilter,
} from 'helpers/dashboardCases';
import { useDebouncedValue } from 'hooks/useCheckEmail';
import { useHasFeature } from 'hooks/useHasFeature';

type PaginationProps = {
    page: number;
    pageSize: number;
    totalRecords: number;
    onPageChange: (pageNumber: number) => void;
};

type MainDashboardCasesTableProps = Omit<CasesTableProps, 'mobile'>;
type DashboardSearchState = Record<DashboardView, string>;

const DEFAULT_LOCAL_PAGE_SIZE = 10;

const MainDashboard = () => {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { permissionsList } = useSelector((state: RootState) => state.permissions);
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const {
        list: clientsApps,
        isLoading: isClientsAppsLoading,
        page,
        pageSize,
        totalRecords,
    } = useSelector((state: StoreRootState) => state.salesClients);
    const [selectedStatus, setSelectedStatus] = useState<string | ClientApplicationStatus>('');
    const [selectedProgram, setSelectedProgram] = useState<string>('');
    const [selectedName, setSelectedName] = useState<string>('');
    const [selectedEmail, setSelectedEmail] = useState<string>('');
    const [selectedPaidClients, setSelectedPaidClients] = useState<string>('');
    const [selectedStatusUpdated, setSelectedStatusUpdated] = useState<string>('');
    const [selectedBOAgent, setSelectedBOAgent] = useState<string>('');
    const [selectedVAgent, setSelectedVAgent] = useState<string>('');
    const [selectedSAgent, setSelectedSAgent] = useState<string>('');
    const [selectedOrganization, setSelectedOrganization] = useState<string>('');
    const [clientsAppsForTable, setClientsAppsForTable] = useState<SalesClient[]>([]);
    const [width, height] = useWindowSize();
    const [mobile, setMobile] = useState(false);
    const [filters, setFilters] = useState<DashboardFilter>({});
    const { activeView, setActiveView } = useDashboardView();
    const [searchByView, setSearchByView] = useState<DashboardSearchState>({
        cases: '',
        clients: '',
        clientOrganizations: '',
    });
    const [organizations, setOrganizations] = useState<DropdownOption[]>([]);
    const [selectedClientOrganization, setSelectedClientOrganization] = useState<string>('');
    const [clients, setClients] = useState<ClientsTableClient[]>([]);
    const [isClientsLoading, setIsClientsLoading] = useState(false);
    const [clientsPage, setClientsPage] = useState(1);
    const [clientsReloadKey, setClientsReloadKey] = useState(0);
    const [companies, setCompanies] = useState<CompaniesTableCompany[]>([]);
    const [isCompaniesLoading, setIsCompaniesLoading] = useState(false);
    const [companiesPage, setCompaniesPage] = useState(1);
    const [organizationFilterOptions, setOrganizationFilterOptions] = useState<DropdownOption[]>(
        []
    );
    const casesSearchInput = searchByView.cases.trim();
    const clientsSearchInput = searchByView.clients.trim();
    const companiesSearchInput = searchByView.clientOrganizations.trim();
    const clientsCandidateNameSearch = clientsSearchInput;
    const localPageSize = DEFAULT_LOCAL_PAGE_SIZE;
    const canOnboardOrganization =
        useHasFeature(FEATURE.PARTNER_ORGANIZATIONS) &&
        permissionsList.some((permission) => permission?.name === PERMISSION.ONBOARD_ORGANIZATIONS);

    const viewOptions: { id: DashboardView; label: string }[] = [
        { id: 'clients', label: t('clients', { lng: language }) },
        { id: 'cases', label: t('cases', { lng: language }) },
        ...(canOnboardOrganization
            ? [
                  {
                      id: 'clientOrganizations' as DashboardView,
                      label: t('clientOrganizations', { lng: language }),
                  },
              ]
            : []),
    ];

    const searchPlaceholderByView: Record<DashboardView, string> = {
        cases: t('clientName', { lng: language }),
        clients: t('clientName', { lng: language }),
        clientOrganizations: t('companyNameOrEmail', { lng: language }),
    };

    useEffect(() => {
        if (!canOnboardOrganization) {
            if (activeView === 'clientOrganizations') {
                setActiveView('clients');
            }
            setOrganizations([]);
            setSelectedOrganization('');
            setSelectedClientOrganization('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeView, canOnboardOrganization]);

    useEffect(() => {
        if (filters.program_name !== undefined) {
            setSelectedProgram(toCasesQueryFilter(filters.program_name));
        }
        if (filters.client_status !== undefined) {
            setSelectedStatus(toCasesQueryFilter(filters.client_status));
        }
        if (filters.clientName) {
            setSelectedName(filters.clientName);
        }
        if (filters.clientEmail !== undefined) {
            setSelectedEmail(toCasesQueryFilter(filters.clientEmail));
        }
        if (filters.paid_clients !== undefined) {
            setSelectedPaidClients(toCasesQueryFilter(filters.paid_clients));
        }
        if (filters.bo_agent !== undefined) {
            setSelectedBOAgent(toCasesQueryFilter(filters.bo_agent));
        }
        if (filters.v_agent !== undefined) {
            setSelectedVAgent(toCasesQueryFilter(filters.v_agent));
        }
        if (filters.s_agent !== undefined) {
            setSelectedSAgent(toCasesQueryFilter(filters.s_agent));
        }
        if (filters.service_provider_id !== undefined) {
            setSelectedOrganization(toCasesQueryFilter(filters.service_provider_id));
        }
        setSelectedStatusUpdated(toCasesQueryFilter(filters.sort_status_updated_at));
    }, [filters]);

    useEffect(() => {
        sessionStorage.removeItem('activeApp');
    }, []);

    useEffect(() => {
        if (!canOnboardOrganization) {
            setOrganizations([]);
            setOrganizationFilterOptions([]);
            return;
        }

        let isCancelled = false;

        const loadOrganizationFilterOptions = async () => {
            try {
                const response = await api.clientServiceProviders.getClientServiceProviders();
                if (isCancelled) return;

                const serviceProviders = response.data || [];
                setOrganizations(
                    serviceProviders
                        .map((organization) => ({
                            value: organization.id.toString(),
                            label:
                                organization.company_name ||
                                organization.legal_entity_name ||
                                organization.email ||
                                `Organization #${organization.id}`,
                        }))
                        .sort((firstOrganization, secondOrganization) =>
                            firstOrganization.label.localeCompare(secondOrganization.label)
                        )
                );

                const seenOrganizationNames = new Set<string>();
                const options = serviceProviders
                    .map((organization) =>
                        (
                            organization.company_name ||
                            organization.legal_entity_name ||
                            organization.email ||
                            ''
                        ).trim()
                    )
                    .filter((companyName) => companyName.length > 0)
                    .filter((companyName) => {
                        const normalizedCompanyName = companyName.toLowerCase();
                        if (seenOrganizationNames.has(normalizedCompanyName)) {
                            return false;
                        }
                        seenOrganizationNames.add(normalizedCompanyName);
                        return true;
                    })
                    .sort((firstCompanyName, secondCompanyName) =>
                        firstCompanyName.localeCompare(secondCompanyName)
                    )
                    .map((companyName) => ({
                        value: companyName,
                        label: companyName,
                    }));

                setOrganizationFilterOptions(options);
            } catch (error) {
                if (!isCancelled) {
                    console.error(error);
                    setOrganizations([]);
                    setOrganizationFilterOptions([]);
                }
            }
        };

        loadOrganizationFilterOptions();

        return () => {
            isCancelled = true;
        };
    }, [canOnboardOrganization]);

    const formatDate = useCallback((dateValue: string | null) => {
        if (!dateValue) return '--';

        const date = new Date(dateValue);
        if (Number.isNaN(date.getTime())) {
            return dateValue;
        }

        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();

        return `${day}/${month}/${year}`;
    }, []);

    const mapClientServiceProviderToCompany = useCallback(
        (company: ClientServiceProvider): CompaniesTableCompany => ({
            id: company.id,
            company_name: company.company_name || '--',
            email: company.email || '--',
            date_added: formatDate(company.created_at),
            number_of_clients: company.clients_count ?? 0,
        }),
        [formatDate]
    );

    const mapClientDirectoryUser = useCallback(
        (client: ClientDirectoryUser): ClientsTableClient => ({
            id: client.id,
            external_id: client.external_id || `USR-${client.id}`,
            service_provider_id: client.service_provider_id ?? null,
            organization_name: client.organization_name || null,
            firstname: client.firstname || '',
            lastname: client.lastname || '',
            email: client.email || '--',
            phone_number: client.phone_number || null,
            completed_assessment: formatDate(client.completed_assessment || null),
            status: client.status || 'INVITED',
        }),
        [formatDate]
    );

    const paginatedClients = useMemo(() => {
        const startIndex = (clientsPage - 1) * localPageSize;
        return clients.slice(startIndex, startIndex + localPageSize);
    }, [clientsPage, clients, localPageSize]);

    const paginatedCompanies = useMemo(() => {
        const startIndex = (companiesPage - 1) * localPageSize;
        return companies.slice(startIndex, startIndex + localPageSize);
    }, [companiesPage, companies, localPageSize]);

    useEffect(() => {
        if (width >= 769) {
            setMobile(false);
        }
        if (width < 769) {
            setMobile(true);
        }
    }, [width, height]);

    useEffect(() => {
        if (!canOnboardOrganization && activeView === 'clients') {
            setSelectedClientOrganization('');
        }
    }, [activeView, canOnboardOrganization]);

    useEffect(() => {
        if (activeView !== 'clients') {
            return;
        }

        let isCancelled = false;

        const loadUsers = async () => {
            try {
                setIsClientsLoading(true);
                const response = await api.clients.getAllClients(
                    clientsCandidateNameSearch,
                    canOnboardOrganization &&
                        selectedClientOrganization &&
                        selectedClientOrganization !== t('all', { lng: language })
                        ? selectedClientOrganization
                        : undefined
                );
                if (isCancelled) return;

                const mappedClients = (response.data || []).map(mapClientDirectoryUser);
                setClients(mappedClients);
            } catch (error) {
                if (isCancelled) return;
                console.error(error);
                setClients([]);
            } finally {
                if (!isCancelled) {
                    setIsClientsLoading(false);
                }
            }
        };

        loadUsers();

        return () => {
            isCancelled = true;
        };
    }, [
        activeView,
        canOnboardOrganization,
        clientsCandidateNameSearch,
        mapClientDirectoryUser,
        selectedClientOrganization,
        language,
        t,
        clientsReloadKey,
    ]);

    useEffect(() => {
        if (activeView !== 'clientOrganizations' || !canOnboardOrganization) {
            return;
        }

        let isCancelled = false;

        const loadCompanies = async () => {
            try {
                setIsCompaniesLoading(true);
                const response = await api.clientServiceProviders.getClientServiceProviders(
                    companiesSearchInput || undefined,
                    true
                );
                if (isCancelled) return;

                setCompanies((response.data || []).map(mapClientServiceProviderToCompany));
            } catch (error) {
                if (isCancelled) return;
                console.error(error);
                setCompanies([]);
            } finally {
                if (!isCancelled) {
                    setIsCompaniesLoading(false);
                }
            }
        };

        loadCompanies();

        return () => {
            isCancelled = true;
        };
    }, [
        activeView,
        canOnboardOrganization,
        companiesSearchInput,
        mapClientServiceProviderToCompany,
    ]);

    const { case_id: resolvedCaseId, external_id: resolvedExternalId } = resolveCasesIdQuery(
        filters.caseId,
        casesSearchInput,
        isCasesIdType(filters.caseIdType) ? filters.caseIdType : 'case'
    );
    const debouncedCaseId = useDebouncedValue(resolvedCaseId, CASES_ID_SEARCH_DEBOUNCE_MS);
    const debouncedExternalId = useDebouncedValue(resolvedExternalId, CASES_ID_SEARCH_DEBOUNCE_MS);

    useEffect(() => {
        setSelectedName(isNumericCaseId(casesSearchInput) ? '' : casesSearchInput);
    }, [casesSearchInput]);

    useEffect(() => {
        setClientsPage(1);
    }, [clientsSearchInput, selectedClientOrganization]);

    useEffect(() => {
        setCompaniesPage(1);
    }, [companiesSearchInput]);

    useEffect(() => {
        const maxClientsPage = Math.max(1, Math.ceil(clients.length / localPageSize));
        if (clientsPage > maxClientsPage) {
            setClientsPage(maxClientsPage);
        }
    }, [clientsPage, clients.length, localPageSize]);

    useEffect(() => {
        const maxCompaniesPage = Math.max(1, Math.ceil(companies.length / localPageSize));
        if (companiesPage > maxCompaniesPage) {
            setCompaniesPage(maxCompaniesPage);
        }
    }, [companiesPage, companies.length, localPageSize]);

    const dispatchCases = useCallback(
        (
            pageVal: number,
            pageSizeVal: number,
            client_statusVal: string,
            selectedPaidClientsVal: string,
            program_nameVal: string,
            case_idVal: string,
            external_idVal: string,
            candidate_nameVal: string,
            candidate_emailVal: string,
            selectedStatusUpdatedVal: string,
            selectedBOAgentVal: string,
            selectedVAgentVal: string,
            selectedSAgentVal: string,
            selectedOrganizationVal: string
        ) => {
            const paginationValues: Record<string, number | string> = {
                page: pageVal,
                pageSize: pageSizeVal,
            };
            if (
                selectedPaidClientsVal !== t('all', { lng: language }) &&
                selectedPaidClientsVal !== ''
            ) {
                paginationValues.payment_status = selectedPaidClientsVal;
            }
            if (selectedBOAgentVal !== t('all', { lng: language }) && selectedBOAgentVal !== '') {
                paginationValues.backoffice_id = selectedBOAgentVal;
            }
            if (selectedVAgentVal !== t('all', { lng: language }) && selectedVAgentVal !== '') {
                paginationValues.consultant_id = selectedVAgentVal;
            }
            if (selectedSAgentVal !== t('all', { lng: language }) && selectedSAgentVal !== '') {
                paginationValues.sales_id = selectedSAgentVal;
            }
            if (
                selectedOrganizationVal !== t('all', { lng: language }) &&
                selectedOrganizationVal !== ''
            ) {
                paginationValues.service_provider_id = selectedOrganizationVal;
            }
            if (
                client_statusVal &&
                client_statusVal !== t('selectStatus', { lng: language }) &&
                client_statusVal !== t('all', { lng: language })
            ) {
                paginationValues.client_status = client_statusVal;
            }
            if (program_nameVal !== t('all', { lng: language }) && program_nameVal !== '') {
                paginationValues.program_name = program_nameVal;
            }
            if (case_idVal) {
                paginationValues.case_id = case_idVal;
            } else if (external_idVal) {
                paginationValues.external_id = external_idVal;
            }
            if (candidate_nameVal) {
                paginationValues.candidate_name = candidate_nameVal;
            }
            if (candidate_emailVal) {
                paginationValues.candidate_email = candidate_emailVal;
            }
            if (selectedStatusUpdatedVal) {
                paginationValues.sort_status_updated_at = selectedStatusUpdatedVal;
            }
            dispatch(loadSalesClients(paginationValues));
        },
        [dispatch, language, t]
    );

    useEffect(() => {
        if (activeView !== 'cases') {
            return;
        }
        dispatchCases(
            1,
            pageSize,
            selectedStatus,
            selectedPaidClients,
            selectedProgram,
            debouncedCaseId,
            debouncedExternalId,
            selectedName,
            selectedEmail,
            selectedStatusUpdated,
            selectedBOAgent,
            selectedVAgent,
            selectedSAgent,
            selectedOrganization
        );
    }, [
        activeView,
        dispatchCases,
        pageSize,
        selectedBOAgent,
        debouncedCaseId,
        debouncedExternalId,
        selectedEmail,
        selectedName,
        selectedPaidClients,
        selectedProgram,
        selectedSAgent,
        selectedStatus,
        selectedStatusUpdated,
        selectedVAgent,
        selectedOrganization,
    ]);

    const handleCasesPageChange = (pageNumber: number) => {
        dispatchCases(
            pageNumber,
            pageSize,
            selectedStatus,
            selectedPaidClients,
            selectedProgram,
            debouncedCaseId,
            debouncedExternalId,
            selectedName,
            selectedEmail,
            selectedStatusUpdated,
            selectedBOAgent,
            selectedVAgent,
            selectedSAgent,
            selectedOrganization
        );
    };

    const reloadCases = () => {
        dispatchCases(
            page,
            pageSize,
            selectedStatus,
            selectedPaidClients,
            selectedProgram,
            debouncedCaseId,
            debouncedExternalId,
            selectedName,
            selectedEmail,
            selectedStatusUpdated,
            selectedBOAgent,
            selectedVAgent,
            selectedSAgent,
            selectedOrganization
        );
    };

    const casesTableProps: MainDashboardCasesTableProps = {
        clientsApps,
        clientsAppsForTable,
        setClientsAppsForTable,
        isClientsAppsLoading,
        reloadClients: reloadCases,
        onFilterChange: setFilters,
        organizations: canOnboardOrganization ? organizations : [],
    };

    const casesPagination: PaginationProps = {
        page,
        pageSize,
        totalRecords,
        onPageChange: handleCasesPageChange,
    };

    const clientsPagination: PaginationProps = {
        page: clientsPage,
        pageSize: localPageSize,
        totalRecords: clients.length,
        onPageChange: setClientsPage,
    };

    const companiesPagination: PaginationProps = {
        page: companiesPage,
        pageSize: localPageSize,
        totalRecords: companies.length,
        onPageChange: setCompaniesPage,
    };

    const renderPagination = ({ page, pageSize, totalRecords, onPageChange }: PaginationProps) => (
        <span
            style={{
                display: 'flex',
                justifyContent: 'center',
                width: '96vw',
            }}
        >
            <PaginatedItems
                itemsPerPage={pageSize}
                numberOfItems={totalRecords}
                handleClick={onPageChange}
                pageNumber={page}
            />
        </span>
    );

    const renderActiveTable = () => {
        if (activeView === 'clients') {
            return (
                <>
                    <UsersTable
                        mobile={mobile}
                        clients={paginatedClients}
                        isLoading={isClientsLoading}
                        organizationFilterOptions={
                            canOnboardOrganization ? organizationFilterOptions : []
                        }
                        selectedOrganizationFilter={
                            canOnboardOrganization ? selectedClientOrganization : ''
                        }
                        onOrganizationFilterChange={setSelectedClientOrganization}
                        onClientUpdated={() => setClientsReloadKey((key) => key + 1)}
                        showOrganizationColumn={canOnboardOrganization}
                    />
                    {renderPagination(clientsPagination)}
                </>
            );
        }

        if (activeView === 'clientOrganizations') {
            return (
                <>
                    <CompaniesTable
                        mobile={mobile}
                        companies={paginatedCompanies}
                        isLoading={isCompaniesLoading}
                    />
                    {renderPagination(companiesPagination)}
                </>
            );
        }

        return (
            <>
                <CasesTable mobile={mobile} {...casesTableProps} />
                {renderPagination(casesPagination)}
            </>
        );
    };

    const handleActiveSearchChange = (value: string) => {
        setSearchByView((currentSearchState) => ({
            ...currentSearchState,
            [activeView]: value,
        }));
    };

    return (
        <>
            <div
                style={{
                    display: 'flex',
                    minHeight: '80px',
                    alignItems: 'center',
                    gap: '1rem',
                    flexWrap: 'wrap',
                    paddingRight: mobile ? 0 : '360px',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem',
                        flexWrap: 'wrap',
                        flex: 1,
                        minWidth: 0,
                    }}
                >
                    <h1 style={{ marginRight: '1rem', display: 'flex', gap: '0.8rem' }}>
                        {t('dashboard', { lng: language })}
                    </h1>
                    <div
                        style={{
                            display: 'inline-flex',
                            padding: '4px',
                            borderRadius: '999px',
                            border: '1px solid #d9d4e8',
                            background: '#f6f2ff',
                            boxShadow: '0 2px 8px rgba(38, 16, 78, 0.08)',
                            gap: '4px',
                        }}
                    >
                        {viewOptions.map((viewOption) => (
                            <button
                                key={viewOption.id}
                                type="button"
                                onClick={() => setActiveView(viewOption.id)}
                                style={{
                                    border: 'none',
                                    borderRadius: '999px',
                                    padding: '8px 16px',
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    background:
                                        activeView === viewOption.id ? '#5a2ea6' : 'transparent',
                                    color: activeView === viewOption.id ? '#ffffff' : '#5a2ea6',
                                }}
                            >
                                {viewOption.label}
                            </button>
                        ))}
                    </div>
                    <SearchClientBar
                        mobile={mobile}
                        value={searchByView[activeView]}
                        onChange={handleActiveSearchChange}
                        placeHolder={searchPlaceholderByView[activeView]}
                        containerStyle={
                            mobile
                                ? { flex: '1 1 100%' }
                                : { maxWidth: '289px', minWidth: '220px', width: '289px' }
                        }
                    />
                </div>
            </div>
            {mobile && activeView === 'cases' && (
                <SearchbarContainerMobileForDashboard>
                    <TopContainer mobile={mobile}>
                        <SelectStatusDropdown
                            dashboard={true}
                            mobile={mobile}
                            selectedStatus={selectedStatus}
                            handleSelectedStatus={setSelectedStatus}
                        />
                        <SelectVisaProgramDropdown
                            dashboard={true}
                            mobile={mobile}
                            selectedProgram={selectedProgram}
                            handleSelectedProgram={setSelectedProgram}
                        />
                    </TopContainer>
                </SearchbarContainerMobileForDashboard>
            )}
            {renderActiveTable()}
        </>
    );
};

export default MainDashboard;
